import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Select as ChakraSelect,
  Spinner,
  Text,
} from "@chakra-ui/react";
import Select from "react-select";
import { queryClient } from "App";
import { postRequest, useMutationWrapper, useQueryWrapper } from "services/apiHelper";
import { categoryRequest } from "services/request";
import { queryKeys } from "services/querykeys";
import { type CategoryGroup, type CategoryType } from "utils/constants";
import { errorToast, successToast } from "utils/hooks";

export interface CategorySelection {
  categoryIds: string[];
  category: string;
  customCategory: string;
  usesOtherCategory: boolean;
}

interface CategoryOption {
  value: string;
  label: string;
  category: CategoryType;
}

interface CategoryGroupOption {
  label: string;
  options: CategoryOption[];
}

interface CategoryMultiSelectProps {
  categoryIds?: Array<string | string[]>;
  category?: string | null;
  customCategory?: string | null;
  categoryNeedsReview?: boolean;
  usesOtherCategory?: boolean;
  label?: string;
  helperText?: string;
  isRequired?: boolean;
  canCreateCategory?: boolean;
  includeOtherCategoryId?: boolean;
  isDisabled?: boolean;
  onChange: (selection: CategorySelection) => void;
}

const CATEGORY_CACHE_TIME = 30 * 60 * 1000;
const OTHER_CATEGORY_NAME = "Other / Not Listed";
const OTHER_CATEGORY_SLUG = "other-not-listed";
const LEGACY_OTHER_CATEGORY = "others";
const CATEGORY_GROUPS: Array<{ label: string; value: CategoryGroup | "" }> = [
  { label: "No group", value: "" },
  { label: "Mass Parts", value: "Mass Parts" },
  { label: "Seasons", value: "Seasons" },
  { label: "Others", value: "Others" },
];

const normalizeCategoryIds = (categoryIds: Array<string | string[]> = []) =>
  categoryIds
    .flatMap((id) => (Array.isArray(id) ? id : [id]))
    .filter((id): id is string => Boolean(id));

const normalizeText = (value?: string | null) => value ?? "";

const isOtherCategory = (category?: CategoryType) =>
  category?.slug === OTHER_CATEGORY_SLUG || category?.name === OTHER_CATEGORY_NAME;

const getPrimaryCategoryName = (categories: CategoryType[]) => categories[0]?.name ?? "";

const hasLegacyMatch = (catalogueCategory: CategoryType, legacyCategory: string) => {
  const normalizedLegacy = legacyCategory.trim().toLowerCase();
  if (!normalizedLegacy) return false;

  return (
    catalogueCategory.name.toLowerCase() === normalizedLegacy ||
    catalogueCategory.slug.toLowerCase() === normalizedLegacy
  );
};

const buildGroupedOptions = (categories: CategoryType[]): CategoryGroupOption[] => {
  const groupMap = new Map<string, CategoryOption[]>();

  categories.forEach((category) => {
    const label = category.group ?? "General";
    const option = {
      value: category.id,
      label: category.name,
      category,
    };

    groupMap.set(label, [...(groupMap.get(label) ?? []), option]);
  });

  return Array.from(groupMap.entries()).map(([label, options]) => ({ label, options }));
};

const CategoryMultiSelect = (props: CategoryMultiSelectProps) => {
  const {
    categoryIds = [],
    category = "",
    customCategory = "",
    categoryNeedsReview = false,
    usesOtherCategory = false,
    label = "Categories",
    helperText,
    isRequired = false,
    canCreateCategory = false,
    includeOtherCategoryId = false,
    isDisabled = false,
    onChange,
  } = props;
  const [newCategoryGroup, setNewCategoryGroup] = useState<CategoryGroup | "">("Others");
  const normalizedCategory = normalizeText(category);
  const normalizedCustomCategory = normalizeText(customCategory);
  const normalizedCategoryIds = useMemo(() => normalizeCategoryIds(categoryIds), [categoryIds]);
  const usesLegacyOther = normalizedCategory.trim().toLowerCase() === LEGACY_OTHER_CATEGORY;
  const shouldUseOtherCategory = usesOtherCategory || usesLegacyOther;

  const { data, isLoading } = useQueryWrapper(queryKeys.CATEGORIES, categoryRequest.CATEGORIES, {
    staleTime: CATEGORY_CACHE_TIME,
    cacheTime: CATEGORY_CACHE_TIME,
    refetchOnWindowFocus: false,
  });

  const categories: CategoryType[] = data?.data ?? [];
  const options = useMemo(() => buildGroupedOptions(categories), [categories]);
  const flatOptions = useMemo(() => options.flatMap((group) => group.options), [options]);
  const otherCategory = categories.find(isOtherCategory);
  const selectedCategories = categories.filter((item) => normalizedCategoryIds.includes(item.id));
  const selectedOptions = flatOptions.filter(
    (option) =>
      normalizedCategoryIds.includes(option.value) ||
      (shouldUseOtherCategory && isOtherCategory(option.category)),
  );
  const nonOtherSelectedCategories = selectedCategories.filter((item) => !isOtherCategory(item));
  const primaryCategory = getPrimaryCategoryName(nonOtherSelectedCategories);
  const showSubmittedCategory =
    Boolean(normalizedCategory.trim()) &&
    normalizedCategory.trim().toLowerCase() !== LEGACY_OTHER_CATEGORY &&
    selectedOptions.length === 0;
  const showCustomCategory = shouldUseOtherCategory || Boolean(normalizedCustomCategory.trim());

  useEffect(() => {
    if (!otherCategory) return;

    if (
      includeOtherCategoryId &&
      shouldUseOtherCategory &&
      !normalizedCategoryIds.includes(otherCategory.id)
    ) {
      onChange({
        categoryIds: [...normalizedCategoryIds, otherCategory.id],
        category: primaryCategory || LEGACY_OTHER_CATEGORY,
        customCategory: normalizedCustomCategory,
        usesOtherCategory: true,
      });
      return;
    }

    if (includeOtherCategoryId || !normalizedCategoryIds.includes(otherCategory.id)) return;

    onChange({
      categoryIds: normalizedCategoryIds.filter((id) => id !== otherCategory.id),
      category: primaryCategory || LEGACY_OTHER_CATEGORY,
      customCategory: normalizedCustomCategory,
      usesOtherCategory: true,
    });
  }, [
    includeOtherCategoryId,
    normalizedCategoryIds,
    normalizedCustomCategory,
    onChange,
    otherCategory,
    primaryCategory,
    shouldUseOtherCategory,
  ]);

  useEffect(() => {
    if (
      normalizedCategoryIds.length > 0 ||
      shouldUseOtherCategory ||
      !normalizedCategory.trim() ||
      categories.length === 0
    ) {
      return;
    }

    const matchedCategory = categories.find((item) => hasLegacyMatch(item, normalizedCategory));
    if (!matchedCategory) return;

    onChange({
      categoryIds:
        isOtherCategory(matchedCategory) && !includeOtherCategoryId ? [] : [matchedCategory.id],
      category: isOtherCategory(matchedCategory) ? LEGACY_OTHER_CATEGORY : matchedCategory.name,
      customCategory: normalizedCustomCategory,
      usesOtherCategory: isOtherCategory(matchedCategory),
    });
  }, [
    categories,
    includeOtherCategoryId,
    normalizedCategory,
    normalizedCategoryIds.length,
    normalizedCustomCategory,
    onChange,
    shouldUseOtherCategory,
  ]);

  const handleSelectChange = (selected: readonly CategoryOption[] | null) => {
    const selectedItems = selected?.map((option) => option.category) ?? [];
    const nonOtherCategories = selectedItems.filter((item) => !isOtherCategory(item));
    const nextUsesOtherCategory = selectedItems.some(isOtherCategory);
    const nextCategoryIds = selectedItems
      .filter((item) => includeOtherCategoryId || !isOtherCategory(item))
      .map((item) => item.id);
    const nextPrimaryCategory = getPrimaryCategoryName(nonOtherCategories);

    onChange({
      categoryIds: nextCategoryIds,
      category: nextUsesOtherCategory ? LEGACY_OTHER_CATEGORY : nextPrimaryCategory,
      customCategory: nextUsesOtherCategory ? normalizedCustomCategory : "",
      usesOtherCategory: nextUsesOtherCategory,
    });
  };

  const onCreateSuccess = (response) => {
    const createdCategory: CategoryType | undefined = response?.data ?? response;
    if (!createdCategory?.id) return;

    void queryClient.invalidateQueries(queryKeys.CATEGORIES);
    successToast("Category created");
    onChange({
      categoryIds: Array.from(new Set([...normalizedCategoryIds, createdCategory.id])),
      category: primaryCategory || createdCategory.name,
      customCategory: "",
      usesOtherCategory: false,
    });
  };

  const { mutate: createCategory, isLoading: isCreatingCategory } = useMutationWrapper(
    postRequest,
    onCreateSuccess,
  );

  const handleCreateCategory = () => {
    const name = normalizedCustomCategory.trim();
    if (!name) {
      errorToast("Enter a category name before creating it");
      return;
    }

    createCategory({
      url: categoryRequest.CATEGORIES,
      data: {
        name,
        group: newCategoryGroup === "" ? null : newCategoryGroup,
      },
    });
  };

  return (
    <FormControl id="category" isRequired={isRequired} mb={4}>
      <FormLabel mb={1}>{label}</FormLabel>
      <Select
        isMulti
        isDisabled={isDisabled}
        isLoading={isLoading}
        options={options}
        value={selectedOptions}
        placeholder="Select categories"
        onChange={handleSelectChange}
      />
      {isLoading && <Spinner size="sm" mt="2" />}
      {helperText && <FormHelperText m={0}>{helperText}</FormHelperText>}
      {categoryNeedsReview && (
        <Alert status="warning" mt="3" borderRadius="md">
          <AlertIcon />
          <AlertDescription>
            This script needs a catalogue category before approval.
          </AlertDescription>
        </Alert>
      )}
      {showSubmittedCategory && (
        <Box mt="3" p="3" borderWidth="1px" borderColor="#fed7aa" borderRadius="md" bg="#fff7ed">
          <Text fontSize="12px" fontWeight="bold" color="#9a3412">
            Submitted category
          </Text>
          <Text fontSize="sm" color="#7c2d12">
            {normalizedCategory}
          </Text>
        </Box>
      )}
      {showCustomCategory && (
        <Box mt="3" p="3" borderWidth="1px" borderColor="#fed7aa" borderRadius="md" bg="#fff7ed">
          <Text fontSize="12px" fontWeight="bold" color="#9a3412">
            Custom category
          </Text>
          <Input
            mt="2"
            isDisabled={isDisabled}
            value={normalizedCustomCategory}
            placeholder="Specify category"
            onChange={(event) => {
              onChange({
                categoryIds: normalizedCategoryIds,
                category: primaryCategory || LEGACY_OTHER_CATEGORY,
                customCategory: event.target.value,
                usesOtherCategory: true,
              });
            }}
          />
          {canCreateCategory && !isDisabled && (
            <Flex
              mt="3"
              gap="2"
              alignItems={{ base: "stretch", sm: "center" }}
              flexDir={{ base: "column", sm: "row" }}
            >
              <ChakraSelect
                value={newCategoryGroup ?? ""}
                onChange={(event) => {
                  setNewCategoryGroup(event.target.value as CategoryGroup | "");
                }}
              >
                {CATEGORY_GROUPS.map((group) => (
                  <option key={group.label} value={group.value ?? ""}>
                    {group.label}
                  </option>
                ))}
              </ChakraSelect>
              <Button
                type="button"
                onClick={handleCreateCategory}
                isLoading={isCreatingCategory}
                flexShrink={0}
              >
                Create category
              </Button>
            </Flex>
          )}
        </Box>
      )}
    </FormControl>
  );
};

export default CategoryMultiSelect;
